import type { UtilitySchedule } from './interfaces/utility.interface';

/**
 * Schedule display helpers for setup, HITL, and billing-period labels.
 *
 * **Two identities** (Issue #127):
 * - **Logical tariff** — ``(utility_id, external_utility_schedule_id)`` on the meter; the API
 *   returns one row per tariff from ``GET …/utility_schedule/catalog`` for typeahead.
 * - **Version row** — ``utility_schedule.id`` on billing periods; every effective-dated row must
 *   remain addressable by id (`mergeUtilityScheduleLists` keys by id, not external id).
 *
 * Use `findUtilityScheduleForMeter` when resolving the meter's current tariff from a list that
 * may contain many version rows for the same external id.
 */

/** Merge schedule lists from multiple utilities; keep every version row (keyed by id). */
export function mergeUtilityScheduleLists(
  ...lists: readonly (readonly UtilitySchedule[])[]
): UtilitySchedule[] {
  const byId = new Map<number, UtilitySchedule>();
  for (const list of lists) {
    for (const row of list) {
      if (row.id != null) {
        byId.set(row.id, row);
      }
    }
  }
  return Array.from(byId.values());
}

function preferCanonicalUtilityScheduleRow(
  a: UtilitySchedule,
  b: UtilitySchedule
): UtilitySchedule {
  const aCur = a.is_current === true;
  const bCur = b.is_current === true;
  if (aCur !== bCur) {
    return bCur ? b : a;
  }
  const aId = a.id ?? 0;
  const bId = b.id ?? 0;
  return bId > aId ? b : a;
}

/** Resolve a meter's tariff row from a merged schedule list. */
export function findUtilityScheduleForMeter(
  schedules: readonly UtilitySchedule[],
  meter: { utility_id?: number | null; external_utility_schedule_id?: string | null }
): UtilitySchedule | undefined {
  const externalId = meter.external_utility_schedule_id?.trim();
  if (!externalId) {
    return undefined;
  }
  const matches = schedules.filter((s) => {
    if (s.external_utility_schedule_id?.trim() !== externalId) {
      return false;
    }
    return meter.utility_id == null || s.utility_id === meter.utility_id;
  });
  if (matches.length === 0) {
    return undefined;
  }
  if (matches.length === 1) {
    return matches[0];
  }
  return matches.reduce(preferCanonicalUtilityScheduleRow);
}

/**
 * Display and light-weight ranking helpers for utility schedule pickers.
 *
 * Ops match bills to the **logical tariff** (`external_utility_schedule_id`) on the meter step.
 * Billing-period HITL (step 3) picks a **version row** (`utility_schedule.id` + effective dates)
 * among siblings sharing that external id.
 */

/** Effective date range for a schedule version row. */
export function formatUtilityScheduleEffectiveRange(schedule: UtilitySchedule): string | null {
  const start = schedule.effective_date?.trim();
  const end = schedule.effective_end_date?.trim();
  if (!start && !end) {
    return null;
  }
  if (start && end) {
    return `${start} – ${end}`;
  }
  if (start) {
    return `from ${start}`;
  }
  return `through ${end}`;
}

/** Label for a version row in period HITL (tariff + effective dates). */
export function formatUtilityScheduleVersionLabel(schedule: UtilitySchedule): string {
  const base = formatUtilityScheduleTypeaheadLabel(schedule);
  const range = formatUtilityScheduleEffectiveRange(schedule);
  return range ? `${base} · ${range}` : base;
}

/** Minimal shape from ``GET …/rate_versions``. */
export interface UtilityScheduleVersionRef {
  utility_schedule_id: number;
  effective_date: string;
  effective_end_date?: string | null;
}

/**
 * Version rows for period HITL: prefer ``rate_versions`` API, else sibling filter on full list.
 */
export function utilityScheduleVersionRows(
  schedules: readonly UtilitySchedule[],
  rateVersions: readonly UtilityScheduleVersionRef[],
  anchorScheduleId: number | null | undefined
): UtilitySchedule[] {
  const anchor = anchorScheduleId != null ? schedules.find(s => s.id === anchorScheduleId) : undefined;

  if (rateVersions.length > 0) {
    const rows: UtilitySchedule[] = [];
    for (const v of rateVersions) {
      const row = schedules.find(s => s.id === v.utility_schedule_id);
      if (row) {
        rows.push(row);
      } else if (anchor) {
        rows.push({
          ...anchor,
          id: v.utility_schedule_id,
          effective_date: v.effective_date,
          effective_end_date: v.effective_end_date ?? undefined
        });
      }
    }
    if (rows.length > 0) {
      return rows.sort((a, b) => (a.effective_date ?? '').localeCompare(b.effective_date ?? ''));
    }
  }

  const siblings = utilityScheduleVersionSiblings(schedules, anchorScheduleId);
  if (siblings.length > 0) {
    return siblings;
  }
  return anchor ? [anchor] : [];
}

/** All version rows for the meter's tariff (same ``external_utility_schedule_id``). */
export function utilityScheduleVersionSiblingsByExternalId(
  schedules: readonly UtilitySchedule[],
  externalUtilityScheduleId: string | null | undefined
): UtilitySchedule[] {
  const ext = externalUtilityScheduleId?.trim();
  if (!ext) {
    return [];
  }
  return schedules
    .filter(s => String(s.external_utility_schedule_id ?? '').trim() === ext)
    .sort((a, b) => (a.effective_date ?? '').localeCompare(b.effective_date ?? ''));
}

/** Version rows for period HITL keyed by meter external tariff id. */
export function utilityScheduleVersionRowsByExternalId(
  schedules: readonly UtilitySchedule[],
  rateVersions: readonly UtilityScheduleVersionRef[],
  externalUtilityScheduleId: string | null | undefined
): UtilitySchedule[] {
  const anchor = utilityScheduleVersionSiblingsByExternalId(
    schedules,
    externalUtilityScheduleId
  )[0];
  return utilityScheduleVersionRows(
    schedules,
    rateVersions,
    anchor?.id ?? null
  );
}

/** All version rows for the meter's tariff (same ``external_utility_schedule_id``). */
export function utilityScheduleVersionSiblings(
  schedules: readonly UtilitySchedule[],
  anchorScheduleId: number | null | undefined
): UtilitySchedule[] {
  if (anchorScheduleId == null || anchorScheduleId <= 0) {
    return [];
  }
  const anchor = schedules.find(s => s.id === anchorScheduleId);
  if (!anchor) {
    return [];
  }
  const extKey = String(anchor.external_utility_schedule_id ?? anchor.id);
  return schedules
    .filter(s => String(s.external_utility_schedule_id ?? s.id) === extKey)
    .sort((a, b) => (a.effective_date ?? '').localeCompare(b.effective_date ?? ''));
}

export function scheduleVersionEffectiveOnDate(
  schedule: UtilitySchedule,
  asOf: string | null | undefined
): boolean {
  const d = asOf?.trim();
  if (!d) {
    return false;
  }
  const start = schedule.effective_date?.trim();
  const end = schedule.effective_end_date?.trim();
  if (start && d < start) {
    return false;
  }
  if (end && d > end) {
    return false;
  }
  return true;
}

/** Best match for period end (mirrors backend ``resolve_schedule_version_for_schedule_id``). */
export function defaultScheduleVersionForPeriodEnd(
  versions: readonly UtilitySchedule[],
  periodEnd: string | null | undefined
): UtilitySchedule | null {
  if (!versions.length) {
    return null;
  }
  const asOf = periodEnd?.trim();
  if (!asOf) {
    return versions[0] ?? null;
  }
  const effective = versions.filter(v => scheduleVersionEffectiveOnDate(v, asOf));
  if (effective.length > 0) {
    return [...effective].sort((a, b) =>
      (b.effective_date ?? '').localeCompare(a.effective_date ?? '')
    )[0]!;
  }
  return versions[0] ?? null;
}

/** Single-line label for typeahead search and display. */
export function formatUtilityScheduleTypeaheadLabel(schedule: UtilitySchedule): string {
  const code = schedule.name?.trim() ?? '';
  const desc = schedule.description?.trim() ?? '';
  const parts: string[] = [];

  if (code && desc) {
    parts.push(`${code} — ${desc}`);
  } else if (desc) {
    parts.push(desc);
  } else if (code) {
    parts.push(code);
  } else {
    parts.push(`Schedule ${schedule.id}`);
  }

  const meta: string[] = [];
  if (schedule.use_type?.trim()) {
    meta.push(schedule.use_type.trim());
  }
  const band = formatUtilityScheduleDemandBand(schedule);
  if (band) {
    meta.push(band);
  }
  if (meta.length > 0) {
    parts.push(meta.join(' · '));
  }

  return parts.join(' | ');
}

/** Demand band for display (e.g. ``0–100 kW``, ``≤100 kW``). */
export function formatUtilityScheduleDemandBand(schedule: UtilitySchedule): string | null {
  const units = schedule.demand_units?.trim() || 'kW';
  const min = schedule.min_demand;
  const max = schedule.max_demand;
  if (min == null && max == null) {
    return null;
  }
  if (min == null && max != null) {
    return `≤${formatDemandNumber(max)} ${units}`;
  }
  if (min != null && max == null) {
    return `≥${formatDemandNumber(min)} ${units}`;
  }
  return `${formatDemandNumber(min!)}–${formatDemandNumber(max!)} ${units}`;
}

function formatDemandNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : String(n);
}

/** Multi-line detail for the selected schedule (properties panel / HITL). */
export function utilityScheduleDetailLines(schedule: UtilitySchedule): string[] {
  const lines: string[] = [];
  const code = schedule.name?.trim();
  const desc = schedule.description?.trim();
  if (code) {
    lines.push(`Schedule code: ${code}`);
  }
  if (desc) {
    lines.push(`Description: ${desc}`);
  }
  if (schedule.use_type?.trim()) {
    lines.push(`Use type: ${schedule.use_type.trim()}`);
  }
  const band = formatUtilityScheduleDemandBand(schedule);
  if (band) {
    lines.push(`Demand band: ${band}`);
  }
  return lines;
}

export interface PrintedScheduleHint {
  utilityCompanyName?: string | null;
  utilityScheduleName?: string | null;
  /** Peak kW from bill extraction when available. */
  peakKw?: number | null;
}

/** Rank schedules against printed bill text (heuristic, all utilities). */
export function rankUtilitySchedulesForPrintedHint(
  schedules: readonly UtilitySchedule[],
  hint: PrintedScheduleHint | null | undefined,
  limit = 5
): UtilitySchedule[] {
  const printed = normalizeSearchText(
    [hint?.utilityScheduleName, hint?.utilityCompanyName].filter(Boolean).join(' ')
  );
  if (!printed && hint?.peakKw == null) {
    return [];
  }

  const printedTokens = significantTokens(printed);
  const printedDemand = parseDemandKwRangeFromText(printed);
  const printedUseType = inferUseTypeFromText(printed);
  const printedVoltage = inferVoltageLevelFromText(printed);

  const scored = schedules.map(s => {
    const haystack = scheduleSearchHaystack(s);
    let score = 0;

    if (printed && haystack.includes(printed)) {
      score += 60;
    }

    for (const token of printedTokens) {
      if (haystack.includes(token)) {
        score += 8;
      }
    }

    if (printedDemand && demandRangeMatchesSchedule(printedDemand, s)) {
      score += 35;
    }

    if (hint?.peakKw != null && peakKwFitsSchedule(hint.peakKw, s)) {
      score += 20;
    }

    if (printedUseType && normalizeSearchText(s.use_type ?? '') === printedUseType) {
      score += 15;
    }

    if (printedVoltage && haystack.includes(printedVoltage)) {
      score += 20;
    }

    return { s, score };
  });

  const minScore = printedTokens.length > 0 || printed ? 8 : 15;
  return scored
    .filter(x => x.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(x => x.s);
}

function scheduleSearchHaystack(schedule: UtilitySchedule): string {
  return normalizeSearchText(
    [schedule.name, schedule.description, schedule.use_type, formatUtilityScheduleDemandBand(schedule)]
      .filter(Boolean)
      .join(' ')
  );
}

function normalizeSearchText(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function significantTokens(text: string): string[] {
  const stop = new Set([
    'the',
    'and',
    'for',
    'with',
    'service',
    'rate',
    'schedule',
    'electric',
    'energy',
    'power',
    'utility',
    'bill',
    'charges',
    'delivery',
    'supply'
  ]);
  return [...new Set(text.split(' ').filter(t => t.length >= 3 && !stop.has(t)))];
}

interface DemandKwRange {
  minKw: number | null;
  maxKw: number | null;
}

/** Parse a demand kW band from free text (e.g. ``0 to 100 kw``, ``101-399 kw``). */
export function parseDemandKwRangeFromText(text: string): DemandKwRange | null {
  const t = text.toLowerCase();
  const rangeMatch = t.match(/(\d+(?:\.\d+)?)\s*(?:to|-|–)\s*(\d+(?:\.\d+)?)\s*k?w\b/);
  if (rangeMatch) {
    return { minKw: Number(rangeMatch[1]), maxKw: Number(rangeMatch[2]) };
  }
  const upToMatch = t.match(/(?:up\s+to|≤|<=)\s*(\d+(?:\.\d+)?)\s*k?w\b/);
  if (upToMatch) {
    return { minKw: null, maxKw: Number(upToMatch[1]) };
  }
  const atLeastMatch = t.match(/(?:≥|>=|at\s+least)\s*(\d+(?:\.\d+)?)\s*k?w\b/);
  if (atLeastMatch) {
    return { minKw: Number(atLeastMatch[1]), maxKw: null };
  }
  return null;
}

function demandRangeMatchesSchedule(range: DemandKwRange, schedule: UtilitySchedule): boolean {
  const sMin = schedule.min_demand ?? null;
  const sMax = schedule.max_demand ?? null;
  if (range.minKw != null && range.maxKw != null) {
    if (sMin != null && sMax != null) {
      return sMin === range.minKw && sMax === range.maxKw;
    }
    if (sMax != null && sMin == null) {
      return sMax === range.maxKw;
    }
  }
  if (range.maxKw != null && sMax != null && sMin == null) {
    return Math.abs(sMax - range.maxKw) < 0.01;
  }
  return false;
}

function peakKwFitsSchedule(peakKw: number, schedule: UtilitySchedule): boolean {
  const min = schedule.min_demand;
  const max = schedule.max_demand;
  if (min != null && peakKw < min) {
    return false;
  }
  if (max != null && peakKw > max) {
    return false;
  }
  return min != null || max != null;
}

function inferUseTypeFromText(text: string): string | null {
  if (/\bresidential\b/.test(text)) {
    return 'residential';
  }
  if (/\bcommercial\b/.test(text)) {
    return 'commercial';
  }
  if (/\bmixed\b/.test(text)) {
    return 'mixed use';
  }
  return null;
}

function inferVoltageLevelFromText(text: string): 'primary' | 'secondary' | null {
  if (/\bprimary\b/.test(text) && !/\bsecondary\b/.test(text)) {
    return 'primary';
  }
  if (/\bsecondary\b/.test(text) && !/\bprimary\b/.test(text)) {
    return 'secondary';
  }
  return null;
}
